import type { AddonAvailabilityPayload, AddonAvailabilityResult, AddonAvailabilityResponse } from '@niarab2c/frontend-commons/src/types/addon';
import callApi from '@niarab2c/frontend-commons/src/util/callApi';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid/non-secure';
import { type Addon } from 'niara-spear-crudmodel';
import { CompleteRootState } from '../../base';
type SearchAddonsReturn = Pick<AddonAvailabilityResponse, 'results' | 'entitiesData'> & {
  searchId;
};
export type SearchAddonsParams = Pick<AddonAvailabilityPayload, 'startDate' | 'endDate' | 'propertyId' | 'roomTypeId'> & Partial<Pick<AddonAvailabilityPayload, 'clientId' | 'searchId'>>;
export const searchAddons = createAsyncThunk<SearchAddonsReturn, SearchAddonsParams, {
  state: CompleteRootState;
}>('addonSearch/searchAddons', async (args, {
  getState,
  dispatch,
  rejectWithValue
}): Promise<SearchAddonsReturn> => {
  const rootState = getState();
  const searchId = args?.searchId ?? nanoid();
  const {
    propertyId,
    roomTypeId,
    startDate,
    endDate
  } = args;
  const landingPageId = rootState.storefrontConfig?.storefront?.landingPageId;

  // no ota-builder, o clientId está no reducer
  // no cotação, o clientId está no reducer
  // no front-unificado, o clientId virá do criteriaForm
  const clientId = rootState.quotation?.quotation?.clientId ?? rootState.core.clientId ?? rootState.storefrontConfig?.storefront?.clientId ?? args?.clientId;
  const addonPayload: AddonAvailabilityPayload = {
    propertyId,
    roomTypeId,
    clientId,
    startDate,
    endDate,
    landingPageId,
    searchId
  };
  return callApi('niara-spear-booking-addons', `/addons/availability`, 'post', {
    body: addonPayload
  }).then(r => {
    // insere o addon que vem no entitiesData para dentro do result
    const results = r?.results?.map(result => ({
      ...result,
      addon: r?.entitiesData?.addons?.[result?.addonId]
    }));
    return {
      searchId,
      ...r,
      results
    };
  }).catch(() => {
    return rejectWithValue({
      searchId,
      results: []
    });
  });
});
export type AddonResult = {
  addon: Addon;
  results: AddonAvailabilityResult[];
};
export type AddonSearchState = {
  searchId?: string;
  results?: AddonResult[];
  inProgress?: boolean;
};
const initState = (): AddonSearchState => ({
  results: []
});
const slice = createSlice({
  name: 'addonSearch',
  initialState: initState(),
  reducers: {},
  extraReducers(builder) {
    builder.addCase(searchAddons.fulfilled, (state, action) => {
      if (action.payload.searchId == state.searchId) {
        // agrupa todos os addons pelo addonId, uma vez que para cada variação retornará um resultado diferente
        const groupedResults = action.payload.results.reduce<Record<string, AddonAvailabilityResult[]>>((acc, result: AddonAvailabilityResult) => {
          const addonId = result.addonId;
          const mandatoryPurchase = result?.addon?.mandatoryPurchase;

          // ignora os addons obrigatórios
          if (mandatoryPurchase) return acc;
          if (!acc[addonId]) {
            acc[addonId] = [];
          }
          acc[addonId].push(result);
          return acc;
        }, {});
        const results = Object.values(groupedResults).map(results => ({
          addon: results[0].addon,
          results
        }));
        return {
          ...state,
          results,
          inProgress: false
        };
      }
      return state;
    });
    builder.addCase(searchAddons.rejected, (state, action) => {
      if (action.meta.arg.searchId == state.searchId) {
        return {
          ...state,
          searchId: action.meta.arg.searchId,
          inProgress: false
        };
      }
      return state;
    });
  }
});
const reducer = slice.reducer;
export default reducer;